<script setup lang="ts">
import { useRouteQuery } from "@vueuse/router";
import { alphabetToCity, prefToAlphabet } from "~/utils/sekou/area";

const props = defineProps<{
  pref?: string;
  city: string;
  // _ 区切りのページ番号が存在するかも？
  off: string;
}>();

const current = useRouteQuery("off", props.off ?? 0, { transform: Number });

let perpage = 30;
if (useIsMobile()) {
  perpage = 12;
}

const { data, pending } = await useFetch("/api/sekou/list", {
  query: {
    pref: props.pref,
    city: props.city,
    off: current,
    perpage,
  },
});

// 検索結果から検索の条件をセットする
useSetSekouQuery(data);

// カテゴリごとの件数がdescriptionに必要
if (data.value && data.value.query) {
  useBreadcrumbs(() => {
    const pref = data.value?.query?.pref ?? "";
    const city = data.value?.query?.city ?? "";

    const breadcrumb = [
      { name: "TOP", path: "/" },
      { name: "施工例", path: "/sekou" },
      { name: pref, path: `/sekou/${prefToAlphabet(pref)}` },
    ];

    // 市区町村があった場合
    if (city) {
      breadcrumb.push({
        name: alphabetToCity(city),
        path: `/sekou/${prefToAlphabet(pref)}_${city}`,
      });
    }

    return breadcrumb;
  });

  useHead({
    title: () =>
      data.value?.query?.pref +
      alphabetToCity(data.value?.query?.city ?? "") +
      `の外構施工例(${data.value!.count}件)`,

    meta: [
      {
        name: "description",
        content: `${
          (data.value?.query?.pref ?? "") +
          alphabetToCity(data.value?.query?.city ?? "")
        }の施工例一覧ページです。${
          current.value > 0 ? `${current.value + 1}ページ ` : ""
        }おしゃれな新築オープン外構工事や目隠しフェンス、玄関アプローチなどの施工例をご覧いただけます。ファミリー庭園ではお客様のご要望に合わせた外構プランをご提案しています。本当に必要なものだけを質の高い工事・低価格でご提供します。ファミリー庭園【正規】`,
      },
    ],
  });
}
</script>

<template>
  <div v-if="data" class="pc:px-8 relative min-h-full mb-16">
    <SekouHeader
      :params="{
        count: data.count,
        current: current,
        max: data.max,
        perPage: data.perpage,
      }"
    />
    <div class="min-h-screen">
      <transition name="fade">
        <ListResponsiveWrapper v-if="!pending" :tablet="3" class="mb-16">
          <SekouDetailLink
            v-for="(s, idx) in data.list"
            :sekou_cd="s.sekou_cd"
            :img_path="s.img_path"
            :title_1="s.title_1"
            :title_2="s.title_2"
            :area_1="s.area_1"
            :area_2="s.area_2"
            :key="s.sekou_cd"
            :index="idx"
          >
            <template #default>
              <p class="px-2 line-clamp-2 mb-2">{{ s.title_2 }}</p>
            </template>
          </SekouDetailLink>
        </ListResponsiveWrapper>
      </transition>
      <div
        v-if="!pending && !data.list.length"
        class="w-full text-center py-32"
      >
        <p class="mb-4">この条件に一致する項目は見つかりませんでした</p>

        <SekouMenuFilterOpen>条件を再設定する</SekouMenuFilterOpen>
      </div>
    </div>
    <Pagination class="mb-4" :current="current" :max="data.max" />

    <NuxtLink to="https://famitei.mobi/contents.php/set_discounts">
      <ImageEl
        class="w-full"
        src="/images/footer/set.png"
        alt="同時購入セット割対象商品を見る"
        width="1000"
        height="154"
        sizes="300px md:600px lg:1000px"
        loading="lazy"
        format="webp"
      />
    </NuxtLink>

    <Loading class="absolute left-1/2 -translate-x-5 top-80" v-if="pending" />
  </div>
</template>
